import type {ReactNode} from 'react';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTrashCan} from '@fortawesome/free-regular-svg-icons';
import {SuggestedProblemEditStatusEnum, type UserMinimal} from '@types';
import {formatDateString} from '@utils/dateFormatter';

import '../../styles/suggestions.scss';

dayjs.extend(relativeTime);

export const SuggestionTimestamp = ({date}: {date: string | null}) =>
    date ? (
        <time dateTime={date} title={formatDateString(date)}>
            {dayjs(date).fromNow()}
        </time>
    ) : null;

export const SuggestionDeleteButton = ({
    onClick,
    disabled,
}: {
    onClick: () => void;
    disabled?: boolean;
}) => {
    const {t} = useTranslation();
    return (
        <button
            type="button"
            className="btn btn-outline-secondary suggestion-delete"
            onClick={onClick}
            disabled={disabled}
            title={t('common.delete')}
            aria-label={t('common.delete')}>
            <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
        </button>
    );
};

export type SuggestionListLayout = (content: {
    filters: ReactNode;
    children: ReactNode;
}) => ReactNode;

export const SuggestionStatusFilters = ({
    value,
    counts,
    onChange,
}: {
    value: SuggestedProblemEditStatusEnum;
    counts?: Partial<Record<SuggestedProblemEditStatusEnum, number>>;
    onChange: (value: SuggestedProblemEditStatusEnum) => void;
}) => {
    const {t} = useTranslation();
    return (
        <div
            className="suggestion-status-toolbar"
            role="group"
            aria-label={t('suggestions.status')}>
            {[
                SuggestedProblemEditStatusEnum.OPEN,
                SuggestedProblemEditStatusEnum.APPROVED,
                SuggestedProblemEditStatusEnum.REJECTED,
            ].map(status => (
                <button
                    key={status}
                    type="button"
                    aria-pressed={value === status}
                    onClick={() => onChange(status)}>
                    {t(`common.${status}`)}
                    <span>{counts?.[status] ?? 0}</span>
                </button>
            ))}
        </div>
    );
};

type SuggestionEntryProps = {
    id: number | null;
    problem: {id: number; imageUrl: string};
    status: SuggestedProblemEditStatusEnum;
    metadata: ReactNode;
    comment: string | null;
    summary?: ReactNode;
    resolvedBy: UserMinimal | null;
    resolvedAt: string | null;
    resolutionComment: string | null;
    actions: ReactNode;
    children?: ReactNode;
};

export const SuggestionEntry = ({
    id,
    problem,
    status,
    metadata,
    comment,
    summary,
    resolvedBy,
    resolvedAt,
    resolutionComment,
    actions,
    children,
}: SuggestionEntryProps) => {
    const {t} = useTranslation();
    return (
        <li className="suggestion-entry" data-suggestion-id={id}>
            <Link
                className="suggestion-entry-preview"
                to={`/problems/${problem.id}`}
                target="_blank"
                rel="noopener noreferrer">
                <img
                    src={problem.imageUrl}
                    alt={`${t('common.problem')} #${problem.id}`}
                    width={80}
                    height={80}
                />
            </Link>
            <div className="suggestion-entry-content">
                <div className="suggestion-entry-heading">
                    <h2>
                        <Link
                            to={`/problems/${problem.id}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            {t('common.problem')} #{problem.id}
                        </Link>
                    </h2>
                    <span
                        className={`suggestion-status suggestion-status-${status}`}>
                        {t(`common.${status}`)}
                    </span>
                    {id !== null && (
                        <span
                            className="suggestion-entry-id"
                            title={`${t('common.id')}: ${id}`}>
                            #{id}
                        </span>
                    )}
                </div>
                <div className="suggestion-entry-meta">{metadata}</div>
                {comment && (
                    <p className="suggestion-entry-comment">{comment}</p>
                )}
                {summary && (
                    <div className="suggestion-entry-summary">{summary}</div>
                )}
                {status !== SuggestedProblemEditStatusEnum.OPEN &&
                    (resolvedBy || resolvedAt || resolutionComment) && (
                        <div className="suggestion-entry-resolution">
                            {(resolvedBy || resolvedAt) && (
                                <div className="suggestion-entry-meta">
                                    {resolvedBy && (
                                        <span>
                                            {t('common.resolved_by')}{' '}
                                            <Link
                                                to={`/users/${resolvedBy.name}`}>
                                                {resolvedBy.name}
                                            </Link>
                                        </span>
                                    )}
                                    <SuggestionTimestamp date={resolvedAt} />
                                </div>
                            )}
                            {resolutionComment && (
                                <p>
                                    <span>
                                        {t('suggestions.resolution_comment')}
                                        :{' '}
                                    </span>
                                    {resolutionComment}
                                </p>
                            )}
                        </div>
                    )}
            </div>
            <div className="suggestion-entry-actions">{actions}</div>
            {children && (
                <div className="suggestion-entry-form">{children}</div>
            )}
        </li>
    );
};
